import { captureMessage } from '@sentry/react';
import { useEffect, useMemo } from 'react';
import { useOpenAIChatCompletions } from '../useOpenAIChatCompletions';
import { WISDOM_FOLLOWUP_OPENAI_PROMPT } from './prompt';

interface useFetchWisdomFollowUpsParms {
  prompt: string;
  responseMarkdown: string;
}

interface WisdomFollowUpResponse {
  followupType: string;
  followUpContent: string;
}

export const useFetchWisdomFollowUps = ({
  prompt,
  responseMarkdown,
}: useFetchWisdomFollowUpsParms) => {
  const [
    fetchChatResponse,
    {
      chatResponse,
      isLoading: isLoadingOpenAIResponse,
      error: chatCompletionError,
    },
  ] = useOpenAIChatCompletions();

  useEffect(() => {
    if (prompt && responseMarkdown) {
      fetchChatResponse({
        model: 'gpt-4o',
        messages: [
          { role: 'system', content: WISDOM_FOLLOWUP_OPENAI_PROMPT },
          {
            role: 'user',
            content: `user's question:: ${prompt}\nWisdom's response:: ${responseMarkdown}`,
          },
        ],
      });
    }
  }, [fetchChatResponse, prompt, responseMarkdown]);

  const followups = useMemo(() => {
    if (!chatResponse || isLoadingOpenAIResponse) {
      return;
    }

    const { content } = chatResponse;

    try {
      const formattedContent = content.replace(/```json|```/g, '');
      const parsedResponse: { followUps: WisdomFollowUpResponse[] } =
        JSON.parse(formattedContent);
      return parsedResponse.followUps;
    } catch (err) {
      captureMessage('Error while parsing Wisdom followup OpenAI response', {
        extra: {
          content,
          err,
        },
        level: 'info',
      });
    }
  }, [chatResponse, isLoadingOpenAIResponse]);

  return {
    followups,
    loading: isLoadingOpenAIResponse,
    error: chatCompletionError,
  } as const;
};
